"use client"

// APK self-update surface. Renders nothing on web/PWA and on up-to-date APKs
// (checkForAppUpdate resolves null there). Two shapes:
//   soft      — dismissible bottom banner ("Update" / "Later"; Later snoozes
//               this version for 24h via lib/app-update.ts)
//   mandatory — full-screen blocking overlay (build below the server's
//               APK_UPDATE_MANDATORY_BELOW floor). Not dismissible; the user
//               can still use the browser/PWA, but this APK build is done.
//
// The Update button hands the APK URL to the system browser (see
// openApkDownload) — Android's download + package-installer flow takes over.

import { useEffect, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { Download } from "lucide-react"
import type { UpdateDecision } from "@/lib/app-update"

// How long the "Downloading…" acknowledgement stays on screen before the soft
// banner retires itself. Long enough to be read as confirmation that the tap
// landed, short enough not to sit over the trading UI.
const DISMISS_DELAY_MS = 2500

export function AppUpdateBanner() {
  const [update, setUpdate] = useState<UpdateDecision | null>(null)
  const [downloading, setDownloading] = useState(false)
  // Synchronous re-entry guard for startDownload — see the comment there.
  const downloadStartedRef = useRef(false)

  useEffect(() => {
    let alive = true
    // Dynamic import: keeps the update client (and its @capacitor/app
    // import) off the critical path and out of first-load JS.
    import("@/lib/app-update")
      .then((m) => m.checkForAppUpdate())
      .then((decision) => {
        if (alive && decision) setUpdate(decision)
      })
      .catch(() => {
        /* best-effort — never let the update check surface an error */
      })
    return () => {
      alive = false
    }
  }, [])

  if (!update) return null

  // Handing the APK to Android's download stack produces no signal the web
  // layer can await — the browser/DownloadManager owns it from here, and the
  // install happens outside this WebView entirely. So the banner acknowledges
  // the tap itself: swap to a "Downloading…" label, then step out of the way.
  //
  // Soft banner only. The mandatory overlay stays put and merely shows the
  // label — dismissing a blocking overlay because the user tapped once would
  // let them keep trading on a build the platform has retired.
  const startDownload = () => {
    // Ref, not the `downloading` state, because two taps inside one render
    // both read the state value captured by THAT render — still false for the
    // second tap, since React has not re-rendered (and re-disabled the button)
    // yet. A ref mutates synchronously, so the second tap sees the first.
    // Without this a double-tap queues two APK downloads.
    if (downloadStartedRef.current) return
    downloadStartedRef.current = true
    setDownloading(true)
    void import("@/lib/app-update").then((m) => {
      m.openApkDownload(update.apkUrl)
      if (!update.mandatory) m.dismissUpdateForSession(update.latestVersionCode)
    })
    if (!update.mandatory) {
      window.setTimeout(() => setUpdate(null), DISMISS_DELAY_MS)
    }
  }

  if (update.mandatory) {
    return (
      <div className="fixed inset-0 z-[200] flex items-center justify-center bg-background/95 p-6 backdrop-blur-sm">
        <div className="w-full max-w-sm rounded-lg border bg-card p-6 text-center text-card-foreground shadow-lg">
          <Download className="mx-auto mb-3 h-8 w-8 text-primary" aria-hidden />
          <h2 className="mb-2 text-lg font-semibold">Update required</h2>
          <p className="mb-5 text-sm text-muted-foreground">
            This version of the app is no longer supported. Please install the
            latest version to continue trading.
          </p>
          <Button
            className="w-full"
            onClick={startDownload}
            disabled={downloading}
            data-track="apk-update-mandatory"
          >
            {downloading ? "Downloading…" : "Download update"}
          </Button>
          <p className="mt-3 text-xs text-muted-foreground">
            {downloading
              ? "Check your notifications for download progress, then open the file to install."
              : "After the download finishes, open the file to install."}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-x-0 bottom-0 z-[150] border-t bg-card p-3 text-card-foreground shadow-lg">
      <div className="mx-auto flex max-w-md items-center gap-3">
        <Download className="h-5 w-5 shrink-0 text-primary" aria-hidden />
        <p className="flex-1 text-sm">
          {downloading
            ? "Download started — check your notifications to install."
            : "A new version of the app is available."}
        </p>
        <Button
          size="sm"
          onClick={startDownload}
          disabled={downloading}
          data-track="apk-update-soft"
        >
          {downloading ? "Downloading…" : "Update"}
        </Button>
        {/* "Later" is unchanged: a 24h snooze for this version, distinct from
            the session-only dismissal that Update performs. Hidden once the
            download is under way — snoozing at that point would be meaningless
            and the banner is about to retire itself anyway. */}
        {!downloading && (
          <Button
            size="sm"
            variant="ghost"
            data-track="apk-update-later"
            onClick={() => {
              void import("@/lib/app-update").then((m) => m.snoozeUpdate(update.latestVersionCode))
              setUpdate(null)
            }}
          >
            Later
          </Button>
        )}
      </div>
    </div>
  )
}
